import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Video as VideoIcon, Settings, Trash2, Play , Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FlowNodeProps, ContentBlock } from '@/types/flow';
import BlocksPreview from './BlocksPreview';

const VideoNode = ({ data, selected }: FlowNodeProps) => {
    const config = data.config || {};
    const hasVideo = !!config.media_url;

    return (
        <div
            className={`
                bg-white rounded-lg border-l-[3px] border-l-rose-500 shadow-sm min-w-[260px] max-w-[300px]
                transition-all duration-150
                ${selected ? 'shadow-md ring-2 ring-blue-400/40' : 'border-t border-r border-b border-gray-200 hover:shadow-md'}
            `}
            style={{ borderTop: selected ? undefined : '1px solid #e5e7eb', borderRight: selected ? undefined : '1px solid #e5e7eb', borderBottom: selected ? undefined : '1px solid #e5e7eb' }}
        >
            {/* Header */}
            <div className="px-3 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-rose-50 rounded-lg flex items-center justify-center">
                        <VideoIcon className="w-3.5 h-3.5 text-rose-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="font-semibold text-[13px] text-gray-800 block truncate">{data.node_name || 'Vídeo'}</span>
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Vídeo</span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-3 py-2.5">
                {hasVideo ? (
                    <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden border border-gray-100">
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow">
                                <Play className="w-5 h-5 text-gray-900 ml-0.5" />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="aspect-video bg-gray-50 rounded-lg flex items-center justify-center border border-dashed border-gray-200">
                        <div className="text-center">
                            <VideoIcon className="w-8 h-8 text-gray-300 mx-auto mb-1" />
                            <p className="text-[11px] text-gray-400">Sem vídeo</p>
                        </div>
                    </div>
                )}
            </div>

            <BlocksPreview blocks={(config.blocks || []) as ContentBlock[]} />

            <div className="border-t border-gray-100 px-3 py-1.5 flex gap-1">
                <Button size="sm" variant="ghost" className="flex-1 text-gray-500 hover:text-rose-600 hover:bg-rose-50/50 h-7 text-[11px]" onClick={() => data.onEdit?.(data.id)}>
                    <Settings className="w-3 h-3 mr-1" />
                    Editar
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-blue-500 hover:bg-blue-50/50 h-7 px-2" title="Duplicar" onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}>
                    <Copy className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500 hover:bg-red-50/50 h-7 px-2" onClick={() => data.onDelete?.(data.id)}>
                    <Trash2 className="w-3 h-3" />
                </Button>
            </div>

            <Handle type="target" position={Position.Left} id="target" className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            <Handle type="source" position={Position.Right} id="source" className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </div>
    );
};

export default memo(VideoNode);
